/**
 * OpenAI Ads Measurement Pixel.
 *
 * The base code loads on all PUBLIC pages: public/index.html injects it in
 * the <head> at initial load (skipping signed-in app/admin routes), and
 * ensureOpenAiPixel() covers SPA navigation into a public route when the
 * visitor entered on an app route. The pixel automatically captures the
 * "oppref" click-reference from the landing URL into a first-party cookie;
 * assessment CTAs also carry the query string when navigating to
 * /assessment so the parameter survives client-side routing.
 *
 * PRIVACY: no customer data (no email, name, or IDs) is ever passed to
 * init or measure calls.
 */
export const OPENAI_PIXEL_ID = "7SuUHfeDKUsJh8ozVt3fNF";

const APP_ROUTE_PREFIXES = ["/dashboard", "/intake", "/analysis", "/admin", "/briefings", "/diagnostic", "/submit-review"];

const FIRED_KEY = "w3rc_oai_assessment_fired";

export function ensureOpenAiPixel(pathname) {
  if (typeof window === "undefined" || window.__oaiPixelLoaded) return;
  if (APP_ROUTE_PREFIXES.some((p) => pathname.startsWith(p))) return;
  window.__oaiPixelLoaded = true;
  /* Official OpenAI pixel base snippet. */
  (function (w, d, s, u) {
    if (w.oaiq) return;
    var q = function () { q.q.push(arguments); };
    q.q = [];
    w.oaiq = q;
    var js = d.createElement(s);
    js.async = true;
    js.src = u;
    var f = d.getElementsByTagName(s)[0];
    f.parentNode.insertBefore(js, f);
  })(window, document, "script", "https://bzrcdn.openai.com/sdk/oaiq.min.js");
  window.oaiq("init", { pixelId: OPENAI_PIXEL_ID });
}

/**
 * Fires the standard "lead_created" conversion exactly once, at the moment a
 * visitor completes the four-question assessment AND submits their email.
 * Never fires on page load; a session guard prevents duplicates from repeat
 * submissions. No customer data is included, and standard events take no
 * fourth-argument metadata object.
 */
export function fireAssessmentConversion() {
  if (typeof window === "undefined") return;
  try {
    if (window.sessionStorage.getItem(FIRED_KEY)) return;
    window.sessionStorage.setItem(FIRED_KEY, "1");
  } catch {
    /* storage unavailable: still at most once per pageview via submit flow */
  }
  if (typeof window.oaiq !== "function") return; // pixel not loaded on this entry path
  window.oaiq("measure", "lead_created", { type: "customer_action" });
}

/**
 * Mid-funnel conversions used to measure assessment drop-off:
 *   assessment_started   -> visitor answered the first question and advanced
 *   assessment_completed -> visitor answered all four and reached the result
 * Each fires at most once per visit (sessionStorage guard) and carries NO
 * customer data. They are custom events, separate from lead_created, so they
 * can be optimized independently in OpenAI Ads Manager. No-op if the pixel did
 * not load on this entry path.
 */
function _fireOnce(key, customEventName) {
  if (typeof window === "undefined") return;
  try {
    if (window.sessionStorage.getItem(key)) return;
    window.sessionStorage.setItem(key, "1");
  } catch {
    /* storage unavailable: still at most once per pageview via the quiz flow */
  }
  if (typeof window.oaiq !== "function") return;
  window.oaiq("measure", "custom", { type: "custom" }, { custom_event_name: customEventName });
}

export function fireAssessmentStartedConversion() {
  _fireOnce("w3rc_oai_started_fired", "assessment_started");
}

export function fireAssessmentCompletedConversion() {
  _fireOnce("w3rc_oai_completed_fired", "assessment_completed");
}

/**
 * Fires the "paid_signup" custom conversion at the moment a Stripe payment is
 * confirmed on /checkout/success, exactly once per checkout session (the
 * session id is only used locally as a duplicate guard and is never sent to
 * OpenAI). Zero-amount comp-code checkouts are excluded so ads optimize on
 * revenue. No customer data is included.
 */
export function firePaidSignupConversion(sessionId, amountTotal) {
  if (typeof window === "undefined" || !sessionId) return;
  if (!(amountTotal > 0)) return; // comp codes are not revenue conversions
  try {
    if (window.localStorage.getItem("w3rc_oai_paid_fired") === sessionId) return;
    window.localStorage.setItem("w3rc_oai_paid_fired", sessionId);
  } catch {
    /* storage unavailable: still at most once per confirmation flow */
  }
  if (typeof window.oaiq !== "function") return;
  window.oaiq("measure", "custom", { type: "custom" }, { custom_event_name: "paid_signup" });
}
